import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { RecaptchaVerifier, signInWithPhoneNumber, signInWithPopup, signInWithRedirect, getRedirectResult, GoogleAuthProvider } from 'firebase/auth';
import { auth } from '@/config/firebase';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import Logo from '@/components/Logo';
import { useAuth } from '@/context/AuthContext';
import { X, ShoppingBag, Store } from 'lucide-react';
import { trackPageVisit, trackPwaInstall } from '@/utils/analytics';
import { shouldUseAuthRedirect, hasNativeGoogleBridge } from '@/utils/platform';
import { signInWithGoogleNative } from '@/utils/nativeGoogleAuth';

const PENDING_GOOGLE_ROLE_KEY = 'mylocalgo_pending_google_role';

const Login = () => {
  const navigate = useNavigate();
  const { userCity } = useAuth();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [recaptchaSolved, setRecaptchaSolved] = useState(false);
  const recaptchaRendered = useRef(false);

  // Detect device type
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);

  // Listen for beforeinstallprompt event
  useEffect(() => {
    trackPageVisit('login');
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  // Complete Google sign-in when returning from the redirect flow (iOS wrapper)
  useEffect(() => {
    const pendingRole = localStorage.getItem(PENDING_GOOGLE_ROLE_KEY);
    if (!pendingRole) return;

    (async () => {
      try {
        setLoading(true);
        const result = await getRedirectResult(auth);
        localStorage.removeItem(PENDING_GOOGLE_ROLE_KEY);
        if (result?.user) {
          await finishGoogleLogin(result.user, pendingRole);
        }
      } catch (err) {
        console.error('Error completing Google redirect sign-in:', err);
        localStorage.removeItem(PENDING_GOOGLE_ROLE_KEY);
        setError(err.message || 'Failed to sign in with Google. Please try again.');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize reCAPTCHA on component mount
  useEffect(() => {
    if (recaptchaRendered.current) return;
    
    const setupRecaptcha = () => {
      try {
        // Clear any existing verifier
        if (window.recaptchaVerifier) {
          window.recaptchaVerifier.clear();
          window.recaptchaVerifier = null;
        }

        window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
          size: 'normal',
          callback: (response) => {
            console.log('reCAPTCHA solved successfully');
            setRecaptchaSolved(true);
            setError('');
          },
          'expired-callback': () => {
            console.log('reCAPTCHA expired');
            setRecaptchaSolved(false);
            setError('reCAPTCHA expired. Please solve it again.');
          }
        });

        window.recaptchaVerifier.render().then((widgetId) => {
          console.log('reCAPTCHA rendered, widget ID:', widgetId);
          window.recaptchaWidgetId = widgetId;
          recaptchaRendered.current = true;
        });
      } catch (err) {
        console.error('Error setting up reCAPTCHA:', err);
        setError('Failed to load verification. Please refresh the page.');
      }
    };

    // Small delay to ensure container is in DOM
    const timer = setTimeout(setupRecaptcha, 500);
    
    return () => {
      clearTimeout(timer);
      if (window.recaptchaVerifier) {
        try {
          window.recaptchaVerifier.clear();
        } catch (e) {}
        window.recaptchaVerifier = null;
      }
    };
  }, []);

  // Check for saved role preference
  const getSavedRole = () => {
    return localStorage.getItem('mylocalgo_user_role');
  };

  // Save role preference
  const saveRole = (role) => {
    localStorage.setItem('mylocalgo_user_role', role);
  };

  // Handle phone number submission - shows role modal first
  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!phoneNumber || phoneNumber.length < 10) {
      setError('Please enter a valid phone number');
      return;
    }

    if (!recaptchaSolved) {
      setError('Please complete the reCAPTCHA verification first');
      return;
    }

    // Check if role is already saved
    const savedRole = getSavedRole();
    if (savedRole) {
      // Proceed directly with saved role
      await proceedWithPhoneLogin(savedRole);
    } else {
      // Show role selection modal
      setPendingAction('phone');
      setShowRoleModal(true);
    }
  };

  // Actually perform phone login after role selection
  const proceedWithPhoneLogin = async (role) => {
    try {
      setLoading(true);
      setError('');
      
      if (!window.recaptchaVerifier) {
        setError('Verification not ready. Please refresh the page.');
        setLoading(false);
        return;
      }
      
      console.log('Sending OTP to:', phoneNumber);
      const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, window.recaptchaVerifier);
      
      // Store confirmation result in window for OTP verification
      window.confirmationResult = confirmationResult;
      
      // Navigate to appropriate OTP verification page based on role
      if (role === 'merchant') {
        navigate('/captain/otp-verification', { state: { phoneNumber } });
      } else {
        navigate('/otp-verification', { state: { phoneNumber } });
      }
    } catch (err) {
      console.error('Error sending OTP:', err);
      console.error('Error code:', err.code);
      
      let errorMessage = 'Failed to send OTP. Please try again.';
      if (err.code === 'auth/invalid-phone-number') {
        errorMessage = 'Invalid phone number format.';
      } else if (err.code === 'auth/too-many-requests') {
        errorMessage = 'Too many attempts. Please try again later.';
      } else if (err.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      setError(errorMessage);
      
      // Reset reCAPTCHA state
      setRecaptchaSolved(false);
      recaptchaRendered.current = false;
      
      // Reset reCAPTCHA widget
      if (window.grecaptcha && window.recaptchaWidgetId !== undefined) {
        try {
          window.grecaptcha.reset(window.recaptchaWidgetId);
        } catch (e) {
          console.log('Error resetting reCAPTCHA:', e);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle Google button click - shows role modal first
  const handleGoogleClick = () => {
    setError('');
    
    // Check if role is already saved
    const savedRole = getSavedRole();
    if (savedRole) {
      // Proceed directly with saved role
      proceedWithGoogleLogin(savedRole);
    } else {
      // Show role selection modal
      setPendingAction('google');
      setShowRoleModal(true);
    }
  };

  // Actually perform Google login after role selection
  const proceedWithGoogleLogin = async (role) => {
    try {
      setLoading(true);

      // iOS native wrapper: WKWebView can't complete Firebase's web OAuth handoff,
      // so the app does Google Sign-In natively and hands us the ID token.
      if (hasNativeGoogleBridge()) {
        const nativeResult = await signInWithGoogleNative();
        await finishGoogleLogin(nativeResult.user, role);
        return;
      }

      const provider = new GoogleAuthProvider();

      // Installed iOS PWA: WKWebView blocks OAuth popups -> use redirect
      if (shouldUseAuthRedirect()) {
        localStorage.setItem(PENDING_GOOGLE_ROLE_KEY, role || 'shopper');
        await signInWithRedirect(auth, provider);
        return; // browser navigates away; result handled on return
      }

      const result = await signInWithPopup(auth, provider);
      await finishGoogleLogin(result.user, role);
    } catch (err) {
      console.error('Error with Google login:', err);
      localStorage.removeItem(PENDING_GOOGLE_ROLE_KEY);
      setError(err.message || 'Failed to sign in with Google. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Navigate after a successful Google sign-in (popup or redirect)
  const finishGoogleLogin = async (googleUser, role) => {
    console.log('Google login successful:', googleUser);

    // Navigate based on role
    if (role === 'merchant') {
      // For merchant, check if already registered
      try {
        const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
        const response = await fetch(`${API}/captain/profile/${googleUser.uid}`);

        if (response.ok) {
          const data = await response.json();
          if (data && data.business_name) {
            // Existing merchant - go to dashboard
            console.log('Existing merchant found, navigating to dashboard');
            navigate('/captain/dashboard');
            return;
          }
        } else if (response.status === 404) {
          // Profile not found - new merchant, go to registration
          console.log('No merchant profile found (404), navigating to registration');
          navigate('/captain/register', { state: { from: '/home' } });
          return;
        } else {
          // Other HTTP errors - still try registration but log the issue
          console.error('Profile check returned unexpected status:', response.status);
          navigate('/captain/register', { state: { from: '/home' } });
          return;
        }
      } catch (profileErr) {
        // Network error - show error to user instead of silently redirecting
        console.error('Network error checking merchant profile:', profileErr);
        setError('Network error. Please check your connection and try again.');
        return;
      }
      // Fallback to registration (shouldn't reach here normally)
      navigate('/captain/register', { state: { from: '/home' } });
    } else {
      // For shopper, go to home or city selection
      if (userCity) {
        navigate('/home');
      } else {
        navigate('/city-selection');
      }
    }
  };

  // Handle role selection from modal
  const handleRoleSelection = (role) => {
    saveRole(role);
    setShowRoleModal(false);
    
    if (pendingAction === 'phone') {
      proceedWithPhoneLogin(role);
    } else if (pendingAction === 'google') {
      proceedWithGoogleLogin(role);
    }
    
    setPendingAction(null);
  };

  // Handle PWA installation
  const handleInstallClick = async () => {
    // If browser supports beforeinstallprompt and we have the event
    if (deferredPrompt) {
      // Show the install prompt
      deferredPrompt.prompt();
      
      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
        trackPwaInstall();
      }
      
      // Clear the deferredPrompt
      setDeferredPrompt(null);
    } else {
      // Show manual installation instructions modal
      setShowInstallModal(true);
    }
  };

  return (
    <div 
      className="min-h-screen flex flex-col items-center justify-center p-4"
      style={{
        background: 'linear-gradient(135deg, #43A047 0%, #66BB6A 25%, #9CCC65 50%, #CDDC39 75%, #FDD835 100%)'
      }}
      data-testid="login-page"
    >
      {/* Logo */}
      <div className="mb-8 animate-fade-in">
        <Logo size="medium" />
      </div>

      {/* Login Card */}
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-8 animate-fade-in" style={{ animationDelay: '0.2s' }}>
        <h1 className="text-2xl font-bold text-gray-800 text-center mb-2" data-testid="login-heading">
          Log in or sign up
        </h1>
        <p className="text-gray-500 text-center mb-6 text-sm">
          Enter your phone number to continue
        </p>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm" data-testid="error-message">
            {error}
          </div>
        )}

        {/* Phone Number Form */}
        <form onSubmit={handlePhoneSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number
            </label>
            <PhoneInput
              international
              defaultCountry="IN"
              value={phoneNumber}
              onChange={setPhoneNumber}
              placeholder="Enter phone number"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              data-testid="phone-input"
            />
          </div>

          {/* reCAPTCHA container - visible */}
          <div className="mb-4 flex justify-center">
            <div id="recaptcha-container"></div>
          </div>

          {/* Continue Button */}
          <button
            type="submit"
            disabled={loading || !phoneNumber}
            className="w-full py-3 rounded-xl font-semibold text-white shadow-lg transition-all duration-300 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: 'linear-gradient(90deg, #43A047 0%, #66BB6A 50%, #9CCC65 100%)'
            }}
            data-testid="continue-button"
          >
            {loading ? 'Sending OTP...' : 'Continue'}
          </button>
        </form>

        {/* OR Divider */}
        <div className="flex items-center my-6">
          <div className="flex-1 border-t border-gray-300"></div>
          <span className="px-4 text-gray-500 text-sm font-medium">OR</span>
          <div className="flex-1 border-t border-gray-300"></div>
        </div>

        {/* Google Login Button */}
        <button
          onClick={handleGoogleClick}
          disabled={loading}
          className="w-full py-3 px-4 border-2 border-gray-300 rounded-xl font-semibold text-gray-700 bg-white hover:bg-gray-50 transition-all duration-300 flex items-center justify-center space-x-3 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="google-login-button"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          <span>Continue with Google</span>
        </button>

        {/* Change Role Link - only show if role is saved */}
        {getSavedRole() && (
          <p className="text-xs text-gray-500 text-center mt-4">
            Continuing as <span className="font-semibold capitalize">{getSavedRole()}</span>.{' '}
            <button
              onClick={() => {
                localStorage.removeItem('mylocalgo_user_role');
                // Force re-render
                window.location.reload();
              }}
              className="text-green-600 hover:underline font-medium"
              data-testid="change-role-link"
            >
              Change role
            </button>
          </p>
        )}

        {/* Terms and Privacy */}
        <p className="text-xs text-gray-500 text-center mt-6">
          By continuing, you agree to our{' '}
          <a href="/terms" className="text-green-600 hover:underline" data-testid="terms-link">Terms of Service</a>
          {' '}and{' '}
          <a href="/privacy" className="text-green-600 hover:underline" data-testid="privacy-link">Privacy Policy</a>
        </p>

        {/* PWA Install Button - Always show with smart handling */}
        <div className="mt-6">
          <button
            onClick={handleInstallClick}
            className="w-full py-3 px-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all flex items-center justify-center space-x-2"
            data-testid="install-pwa-button"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <span>Add MyLocalGo to Home Screen</span>
          </button>
          <p className="text-xs text-gray-500 text-center mt-2">
            Quick access from your mobile home screen
          </p>
        </div>
      </div>

      {/* Role Selection Modal */}
      {showRoleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="role-selection-modal">
          <div className="bg-white rounded-3xl w-full max-w-sm p-6 animate-fade-in shadow-2xl">
            {/* Modal Header */}
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold text-gray-800 mb-2">Continue as</h3>
              <p className="text-gray-500 text-sm">Select how you'd like to use MyLocalGo</p>
            </div>
            
            {/* Role Options */}
            <div className="space-y-3">
              {/* Shopper Option */}
              <button
                onClick={() => handleRoleSelection('shopper')}
                className="w-full p-4 border-2 border-gray-200 rounded-2xl hover:border-green-500 hover:bg-green-50 transition-all flex items-center space-x-4 group"
                data-testid="continue-as-shopper-btn"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow">
                  <ShoppingBag className="w-7 h-7 text-white" />
                </div>
                <div className="text-left flex-1">
                  <p className="font-bold text-gray-800 text-lg">Shopper</p>
                  <p className="text-gray-500 text-sm">Discover deals near you</p>
                </div>
              </button>
              
              {/* Merchant Option */}
              <button
                onClick={() => handleRoleSelection('merchant')}
                className="w-full p-4 border-2 border-gray-200 rounded-2xl hover:border-yellow-500 hover:bg-yellow-50 transition-all flex items-center space-x-4 group"
                data-testid="continue-as-merchant-btn"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow">
                  <Store className="w-7 h-7 text-white" />
                </div>
                <div className="text-left flex-1">
                  <p className="font-bold text-gray-800 text-lg">Merchant</p>
                  <p className="text-gray-500 text-sm">Manage your business offers</p>
                </div>
              </button>
            </div>
            
            {/* Cancel Button */}
            <button
              onClick={() => {
                setShowRoleModal(false);
                setPendingAction(null);
              }}
              className="w-full mt-4 py-2 text-gray-500 text-sm hover:text-gray-700 transition-colors"
              data-testid="cancel-role-selection-btn"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Install Instructions Modal */}
      {showInstallModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-4">
          <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md max-h-[80vh] overflow-y-auto animate-slide-up">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between rounded-t-3xl">
              <h3 className="text-lg font-bold text-gray-800">Add to Home Screen</h3>
              <button 
                onClick={() => setShowInstallModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="px-6 py-4">
              {isIOS ? (
                /* iOS Instructions */
                <div className="space-y-4">
                  <p className="text-gray-600 text-sm">Follow these steps to install MyLocalGo on your iPhone/iPad:</p>
                  
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-green-600 font-bold text-sm">1</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">Tap the Share button</p>
                        <p className="text-sm text-gray-500">Look for the share icon (square with arrow) at the bottom of Safari</p>
                        <div className="mt-2 bg-gray-100 p-3 rounded-lg flex justify-center">
                          <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                          </svg>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-green-600 font-bold text-sm">2</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">Scroll and tap "Add to Home Screen"</p>
                        <p className="text-sm text-gray-500">You may need to scroll down in the share menu</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-green-600 font-bold text-sm">3</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">Tap "Add"</p>
                        <p className="text-sm text-gray-500">MyLocalGo will appear on your home screen</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : isAndroid ? (
                /* Android Instructions */
                <div className="space-y-4">
                  <p className="text-gray-600 text-sm">Follow these steps to install MyLocalGo on your Android device:</p>
                  
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-green-600 font-bold text-sm">1</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">Tap the menu button</p>
                        <p className="text-sm text-gray-500">Look for three dots (⋮) in your browser's top-right corner</p>
                        <div className="mt-2 bg-gray-100 p-3 rounded-lg flex justify-center">
                          <svg className="w-8 h-8 text-gray-600" fill="currentColor" viewBox="0 0 24 24">
                            <circle cx="12" cy="5" r="2"/>
                            <circle cx="12" cy="12" r="2"/>
                            <circle cx="12" cy="19" r="2"/>
                          </svg>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-green-600 font-bold text-sm">2</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">Tap "Add to Home screen" or "Install app"</p>
                        <p className="text-sm text-gray-500">The option name may vary by browser</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-green-600 font-bold text-sm">3</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">Confirm the installation</p>
                        <p className="text-sm text-gray-500">MyLocalGo will be added to your home screen</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* Desktop/Other Instructions */
                <div className="space-y-4">
                  <p className="text-gray-600 text-sm">To install MyLocalGo:</p>
                  
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-green-600 font-bold text-sm">1</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">Look for the install icon</p>
                        <p className="text-sm text-gray-500">In Chrome, look for the install icon in the address bar or use the menu</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-green-600 font-bold text-sm">2</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">Click "Install" when prompted</p>
                        <p className="text-sm text-gray-500">Or use browser menu → "Install MyLocalGo"</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Got it button */}
              <button
                onClick={() => setShowInstallModal(false)}
                className="w-full mt-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all"
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;